'use client';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import type { ProjectNote } from '@/types';
import { formatDate } from 'date-fns';
import { DATE_FORMAT_WITH_TIME } from '@/constants';
import {
    Clock,
    User,
    Lock,
    Unlock,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface NoteViewModalProps {
    note?: ProjectNote;
    isOpen: boolean;
    onClose: () => void;
    onEdit?: (note: ProjectNote) => void;
    onShareToggle?: (note: ProjectNote) => void;
    onDelete?: (note: ProjectNote) => void;
}

export const NoteViewModal = ({
    note,
    isOpen,
    onClose,
}: NoteViewModalProps) => {
    if (!note) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-[98vw] max-w-[98vw] h-[94vh] flex flex-col p-0 overflow-hidden border-none bg-background shadow-2xl rounded-2xl">

                {/* Header */}
                <DialogHeader className="px-12 py-10 border-b bg-muted/20">
                    <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-8">

                        {/* LEFT */}
                        <div className="flex-1 space-y-4">

                            {/* Badges */}
                            <div className="flex items-center gap-3 flex-wrap">
                                <Badge
                                    variant="outline"
                                    className="text-xs uppercase font-bold tracking-widest px-3 h-7 bg-primary/10 text-primary border-primary/20"
                                >
                                    Project Note
                                </Badge>

                                {note.personal ? (
                                    <Badge
                                        variant="outline"
                                        className="text-xs uppercase font-bold tracking-widest px-3 h-7 bg-amber-500/10 text-amber-500 border-amber-500/20 gap-1.5"
                                    >
                                        <Lock className="h-3.5 w-3.5" />
                                        Private
                                    </Badge>
                                ) : (
                                    <Badge
                                        variant="outline"
                                        className="text-xs uppercase font-bold tracking-widest px-3 h-7 bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1.5"
                                    >
                                        <Unlock className="h-3.5 w-3.5" />
                                        Shared
                                    </Badge>
                                )}
                            </div>

                            {/* TITLE FIXED */}
                            <DialogTitle className="text-5xl font-extrabold tracking-tight leading-[1.1] break-words">
                                {note.title}
                            </DialogTitle>

                            {/* META */}
                            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-base text-muted-foreground pt-3">
                                <span className="flex items-center gap-2 font-medium">
                                    <User className="h-4 w-4 text-primary/70" />
                                    {note.created_by_email}
                                </span>

                                <span className="flex items-center gap-2 font-medium">
                                    <Clock className="h-4 w-4 text-primary/70" />
                                    Last updated{' '}
                                    {formatDate(
                                        new Date(note.updatedAt),
                                        DATE_FORMAT_WITH_TIME
                                    )}
                                </span>
                            </div>
                        </div>


                    </div>
                </DialogHeader>

                {/* CONTENT FULL WIDTH */}
                <div className="flex-1 overflow-y-auto px-16 py-14">
                    <ReadOnlyContentDisplay
                        html={note.note}
                        className="prose prose-2xl dark:prose-invert max-w-none
              prose-headings:font-extrabold
              prose-p:leading-relaxed
              prose-img:rounded-2xl
              prose-code:before:content-none
              prose-code:after:content-none"
                    />
                </div>

            </DialogContent>
        </Dialog>
    );
};